import React, { useState, useEffect, useCallback } from 'react';
import client from '../api/client';
import { toISODate } from '../utils/dates';
import DatePicker from '../components/DatePicker';
import TagPicker, { detectTags } from '../components/TagPicker';

const MEAL_TYPES = ['BREAKFAST', 'LUNCH', 'DINNER', 'SNACK'];

const BEVERAGE_OPTIONS = [
  'Coffee', 'Tea', 'Green Tea', 'Water', 'Sparkling Water',
  'Juice', 'Alcohol', 'Soda', 'Milk', 'Kombucha',
];

const BEVERAGE_TAGS = ['Caffeinated', 'Sugary', 'Alcoholic'];

const AUTO_TAGS = {
  'Coffee': ['Caffeinated'],
  'Tea': ['Caffeinated'],
  'Green Tea': ['Caffeinated'],
  'Alcohol': ['Alcoholic'],
  'Soda': ['Sugary', 'Caffeinated'],
  'Juice': ['Sugary'],
};

export default function MealsPage() {
  const [tab, setTab] = useState('meals');
  const [selectedDate, setSelectedDate] = useState(toISODate(new Date()));
  const [meals, setMeals] = useState([]);
  const [beverages, setBeverages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissedTags, setDismissedTags] = useState(new Set());

  const [currentMeal, setCurrentMeal] = useState({
    mealType: MEAL_TYPES[0],
    description: '',
    foodTags: [],
    isAntiInflammatory: null,
    tookAcv: false,
    tookAntiSpike: false,
  });

  const [currentBeverage, setCurrentBeverage] = useState({ name: '', quantity: '', tags: [] });

  const fetchMeals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get('/meals', {
        params: { startDate: selectedDate, endDate: selectedDate },
      });
      setMeals(res.data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => { fetchMeals(); }, [fetchMeals]);

  // Load beverages from the check-in for backward compat display
  useEffect(() => {
    client.get('/checkins', { params: { startDate: selectedDate, endDate: selectedDate } })
      .then(res => {
        const allBeverages = res.data.flatMap(ci =>
          (ci.beverages || []).map(b =>
            typeof b === 'string' ? { name: b, quantity: '', tags: [] } : b
          )
        );
        setBeverages(allBeverages);
      })
      .catch(() => {});
  }, [selectedDate]);

  const addMeal = async () => {
    if (!currentMeal.description.trim()) return;
    try {
      await client.post('/meals', {
        ...currentMeal,
        mealDate: selectedDate,
      });
      setDismissedTags(new Set());
      setCurrentMeal({
        mealType: MEAL_TYPES[0],
        description: '',
        foodTags: [],
        isAntiInflammatory: null,
        tookAcv: false,
        tookAntiSpike: false,
      });
      fetchMeals();
    } catch {
      alert('Failed to add meal.');
    }
  };

  const deleteMeal = async (id) => {
    try {
      await client.delete(`/meals/${id}`);
      fetchMeals();
    } catch {
      alert('Failed to delete meal.');
    }
  };

  const addBeverage = () => {
    if (!currentBeverage.name.trim()) return;
    setBeverages(prev => [...prev, { ...currentBeverage, name: currentBeverage.name.trim() }]);
    setCurrentBeverage({ name: '', quantity: '', tags: [] });
  };

  const removeBeverage = (index) => {
    setBeverages(prev => prev.filter((_, i) => i !== index));
  };

  const toggleBeverageTag = (tag) => {
    setCurrentBeverage(b => ({
      ...b,
      tags: b.tags.includes(tag) ? b.tags.filter(t => t !== tag) : [...b.tags, tag],
    }));
  };

  return (
    <div className="space-y-6 lg:max-w-3xl lg:mx-auto">
      <h1 className="text-2xl font-bold text-white drop-shadow-sm">Meals & Beverages</h1>

      <div>
        <label className="block text-sm font-medium text-white/80 mb-1">Date</label>
        <DatePicker
          value={selectedDate}
          max={toISODate(new Date())}
          onChange={setSelectedDate}
        />
      </div>

      {/* Tab switcher */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setTab('meals')}
          className={`flex-1 py-2.5 rounded-xl font-medium text-sm transition
            ${tab === 'meals'
              ? 'bg-white/20 text-white ring-2 ring-white/30'
              : 'bg-white/10 text-white/60 hover:bg-white/20'
            }`}
        >
          Meals {meals.length > 0 && `(${meals.length})`}
        </button>
        <button
          type="button"
          onClick={() => setTab('beverages')}
          className={`flex-1 py-2.5 rounded-xl font-medium text-sm transition
            ${tab === 'beverages'
              ? 'bg-white/20 text-white ring-2 ring-white/30'
              : 'bg-white/10 text-white/60 hover:bg-white/20'
            }`}
        >
          Beverages {beverages.length > 0 && `(${beverages.length})`}
        </button>
      </div>

      <div className="glass rounded-2xl p-6">
        {/* Meals tab */}
        {tab === 'meals' && (
          <div className="space-y-6">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" />
              </div>
            ) : (
              <>
                {meals.length > 0 && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-white/80">Logged Meals</label>
                    {meals.map((meal) => (
                      <div key={meal.id} className="bg-white/5 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-xs font-semibold uppercase text-white/40">{meal.mealType}</span>
                            <p className="text-sm text-white">{meal.description}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => deleteMeal(meal.id)}
                            className="text-white/40 hover:text-white text-sm"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="flex gap-1 mt-1 flex-wrap">
                          {meal.foodTags?.map(tag => (
                            <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-white/70">
                              {tag.replace(/_/g, ' ')}
                            </span>
                          ))}
                          {meal.tookAcv && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-white/15 text-white/80">ACV</span>
                          )}
                          {meal.tookAntiSpike && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-white/15 text-white/80">Anti-Spike</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="space-y-4 border-t border-white/10 pt-4">
                  <div className="flex gap-2">
                    {MEAL_TYPES.map(type => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setCurrentMeal(m => ({ ...m, mealType: type }))}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition
                          ${currentMeal.mealType === type
                            ? 'bg-white/20 text-white'
                            : 'bg-white/10 text-white/60 hover:bg-white/20'
                          }`}
                      >
                        {type.charAt(0) + type.slice(1).toLowerCase()}
                      </button>
                    ))}
                  </div>

                  <input
                    type="text"
                    placeholder="Describe the meal (e.g., salmon bowl with rice)"
                    value={currentMeal.description}
                    onChange={(e) => {
                      const desc = e.target.value;
                      const autoTags = detectTags(desc).filter(t => !dismissedTags.has(t));
                      setCurrentMeal(m => ({
                        ...m,
                        description: desc,
                        foodTags: [...new Set([...m.foodTags, ...autoTags])],
                      }));
                    }}
                    className="w-full rounded-lg bg-white/10 border-white/20 text-white placeholder-white/40 shadow-sm focus:border-white/40 focus:ring-white/30"
                  />

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setCurrentMeal(m => ({ ...m, tookAcv: !m.tookAcv }))}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition
                        ${currentMeal.tookAcv
                          ? 'bg-white/20 text-white ring-2 ring-white/30'
                          : 'bg-white/10 text-white/60 hover:bg-white/20'
                        }`}
                    >
                      Apple Cider Vinegar
                    </button>
                    <button
                      type="button"
                      onClick={() => setCurrentMeal(m => ({ ...m, tookAntiSpike: !m.tookAntiSpike }))}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition
                        ${currentMeal.tookAntiSpike
                          ? 'bg-white/20 text-white ring-2 ring-white/30'
                          : 'bg-white/10 text-white/60 hover:bg-white/20'
                        }`}
                    >
                      Anti-Spike
                    </button>
                  </div>

                  <TagPicker
                    label="Food Tags"
                    selected={currentMeal.foodTags}
                    onChange={(tags) => {
                      const removed = currentMeal.foodTags.filter(t => !tags.includes(t));
                      if (removed.length > 0) {
                        setDismissedTags(prev => {
                          const next = new Set(prev);
                          removed.forEach(t => next.add(t));
                          return next;
                        });
                      }
                      setCurrentMeal(m => ({ ...m, foodTags: tags }));
                    }}
                  />

                  <button
                    type="button"
                    onClick={addMeal}
                    disabled={!currentMeal.description.trim()}
                    className="w-full py-2 bg-white/20 text-white rounded-lg font-medium hover:bg-white/30 disabled:opacity-30"
                  >
                    Add Meal
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Beverages tab */}
        {tab === 'beverages' && (
          <div className="space-y-6">
            {beverages.length > 0 && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-white/80">Logged Beverages</label>
                {beverages.map((bev, i) => (
                  <div key={i} className="bg-white/5 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-white">{bev.name}</p>
                        {bev.quantity && (
                          <span className="text-xs text-white/40">{bev.quantity}</span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeBeverage(i)}
                        className="text-white/40 hover:text-white text-sm"
                      >
                        Remove
                      </button>
                    </div>
                    {bev.tags?.length > 0 && (
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {bev.tags.map(tag => (
                          <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-white/70">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-4 border-t border-white/10 pt-4">
              <label className="text-sm font-medium text-white/80">Quick Add</label>
              <div className="flex flex-wrap gap-2">
                {BEVERAGE_OPTIONS.map(bev => (
                  <button
                    key={bev}
                    type="button"
                    onClick={() => setCurrentBeverage(b => ({
                      ...b,
                      name: bev,
                      tags: AUTO_TAGS[bev] || b.tags,
                    }))}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition
                      ${currentBeverage.name === bev
                        ? 'bg-white/20 text-white ring-2 ring-white/30'
                        : 'bg-white/10 text-white/60 hover:bg-white/20'
                      }`}
                  >
                    {bev}
                  </button>
                ))}
              </div>

              <input
                type="text"
                placeholder="Beverage name (or pick above)"
                value={currentBeverage.name}
                onChange={(e) => setCurrentBeverage(b => ({ ...b, name: e.target.value }))}
                className="w-full rounded-lg bg-white/10 border-white/20 text-white placeholder-white/40 shadow-sm focus:border-white/40 focus:ring-white/30"
              />

              <input
                type="text"
                placeholder="Quantity (e.g., 1 cup, 12 oz)"
                value={currentBeverage.quantity}
                onChange={(e) => setCurrentBeverage(b => ({ ...b, quantity: e.target.value }))}
                className="w-full rounded-lg bg-white/10 border-white/20 text-white placeholder-white/40 shadow-sm focus:border-white/40 focus:ring-white/30"
              />

              <div className="flex flex-wrap gap-2">
                {BEVERAGE_TAGS.map(tag => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleBeverageTag(tag)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition
                      ${currentBeverage.tags.includes(tag)
                        ? 'bg-white/20 text-white ring-2 ring-white/30'
                        : 'bg-white/10 text-white/60 hover:bg-white/20'
                      }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={addBeverage}
                disabled={!currentBeverage.name.trim()}
                className="w-full py-2 bg-white/20 text-white rounded-lg font-medium hover:bg-white/30 disabled:opacity-30"
              >
                Add Beverage
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
